/**
 * MediaRenderer Component
 * Renders images and videos with smooth crossfade transitions
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { DEFAULT_TRANSITION_DURATION_MS } from '@gallery/shared';

interface MediaRendererProps {
  url: string | null;
  type: 'image' | 'video';
  durationMs: number;
  transition?: 'fade' | 'cut' | 'slide';
  isPlaying: boolean;
  onEnded?: () => void;
  onTimeUpdate?: (currentTimeMs: number) => void;
  onError?: (error: string) => void;
  className?: string;
}

export function MediaRenderer({
  url,
  type,
  durationMs,
  transition = 'fade',
  isPlaying,
  onEnded,
  onTimeUpdate,
  onError,
  className = '',
}: MediaRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [opacity, setOpacity] = useState(0);

  // Handle image display with timer
  useEffect(() => {
    if (type !== 'image' || !url || !isPlaying) return;

    startTimeRef.current = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      onTimeUpdate?.(elapsed);

      if (elapsed >= durationMs) {
        onEnded?.();
        return;
      }

      timerRef.current = requestAnimationFrame(tick);
    };

    timerRef.current = requestAnimationFrame(tick);

    return () => {
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
      }
    };
  }, [type, url, isPlaying, durationMs, onEnded, onTimeUpdate]);

  // Handle video playback
  useEffect(() => {
    if (type !== 'video' || !url || !videoRef.current) return;

    const video = videoRef.current;

    // Check if HLS stream
    const isHls = url.includes('.m3u8') || url.includes('manifest');

    if (isHls && Hls.isSupported()) {
      // Clean up existing HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });

      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoaded(true);
        if (isPlaying) {
          video.play().catch(console.error);
        }
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          onError?.(`HLS error: ${data.type}`);
        }
      });

      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = url;
    } else {
      // Regular video
      video.src = url;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [type, url, onError]);

  // Handle video play/pause
  useEffect(() => {
    if (type !== 'video' || !videoRef.current) return;

    if (isPlaying) {
      videoRef.current.play().catch(console.error);
    } else {
      videoRef.current.pause();
    }
  }, [type, isPlaying]);

  // Handle video time updates
  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      onTimeUpdate?.(videoRef.current.currentTime * 1000);
    }
  }, [onTimeUpdate]);

  // Handle video ended
  const handleVideoEnded = useCallback(() => {
    onEnded?.();
  }, [onEnded]);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  // Handle image error
  const handleImageError = useCallback(() => {
    onError?.('Failed to load image');
  }, [onError]);

  // Handle video error
  const handleVideoError = useCallback(() => {
    onError?.('Failed to load video');
  }, [onError]);

  // Fade in/out effect
  useEffect(() => {
    if (!isLoaded) return;

    // Fade in
    const fadeIn = setTimeout(() => {
      setOpacity(1);
    }, 50);

    return () => {
      clearTimeout(fadeIn);
      // Fade out on unmount (if using fade transition)
      if (transition === 'fade') {
        setOpacity(0);
      }
    };
  }, [isLoaded, transition]);

  if (!url) {
    return (
      <div className={`media-renderer ${className}`}>
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-white/50">No media</div>
        </div>
      </div>
    );
  }

  const transitionStyle =
    transition === 'fade'
      ? {
          opacity,
          transition: `opacity ${DEFAULT_TRANSITION_DURATION_MS}ms ease-in-out`,
        }
      : {};

  return (
    <div ref={containerRef} className={`media-renderer relative ${className}`}>
      {type === 'image' ? (
        <img
          ref={imageRef}
          src={url}
          alt=""
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={transitionStyle}
          className="absolute inset-0 w-full h-full object-contain"
        />
      ) : (
        <video
          ref={videoRef}
          onTimeUpdate={handleVideoTimeUpdate}
          onEnded={handleVideoEnded}
          onLoadedData={() => setIsLoaded(true)}
          onError={handleVideoError}
          style={transitionStyle}
          className="absolute inset-0 w-full h-full object-contain"
          playsInline
          muted
          loop={false}
        />
      )}

      {/* Loading indicator */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

/**
 * Crossfade container for smooth transitions between items
 */
interface CrossfadeContainerProps {
  children: React.ReactNode;
  currentKey: string;
}

export function CrossfadeContainer({ children, currentKey }: CrossfadeContainerProps) {
  return (
    <div key={currentKey} className="crossfade-container absolute inset-0">
      {children}
    </div>
  );
}
